import { Box, Stack, Typography } from '@mui/material';
import {
  Edit,
  EditContextProvider,
  Form,
  NumberInput,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useEditController,
  useNotify,
  useTranslate,
} from 'react-admin';
import { Resources } from '../../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';

export function FeeManagementForm() {
  const translate = useTranslate();
  const notify = useNotify();

  const controller = useEditController({
    resource: Resources.feeManagement,
    id: 1,
  });

  const { record, isLoading, error, save } = controller;

  const mustBeValidFeeFormat = (value: any) => {
    if (value === undefined || value === '') return undefined;
    const number = parseFloat(value);

    if (isNaN(number)) {
      return translate('module.feeManagement.validate.invalidNumber');
    }
    
    if (number < 0) {
      return translate('module.feeManagement.validate.invalidNumber');
    }
    
    if (number === 0) {
      return translate('module.feeManagement.validate.zeroNotAllowed');
    }
    
    const decimalPlaces = (value.toString().split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      return translate('module.feeManagement.validate.maxDecimalPlaces');
    }
    
    if (number < 0.01) {
      return translate('module.feeManagement.validate.minimumValue');
    }
    
    if (number > 2.5) {
      return translate('module.feeManagement.validate.maximumValue');
    }
    return undefined;
  };

  // if (isLoading) return <>Loading...</>;
  // if (error) return <>Error</>;

  return (
    <EditContextProvider value={controller}>
      <Edit
        resource={Resources.feeManagement}
        id={1}
        mutationMode="pessimistic"
        transform={(data) => data}
        mutationOptions={{
          onSuccess: () => {
            notify(translate('module.feeManagement.updateSuccess'), { type: 'success' });
            controller.refetch?.();
          },
          onError: (error: any) => {
            const errorCode = error?.body?.code || error?.code;
            switch (errorCode) {
              case 'INTERNAL_SERVER_ERROR':
                notify(error?.message, { type: 'error' });
                break;
              default:
                notify(error?.message ?? translate('module.feeManagement.unknownErrorUpdate'), {
                  type: 'error',
                });
                break;
            }
          },
        }}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          p: 0,
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <Form defaultValues={record} disableInvalidFormNotification>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
          >
            <Typography variant="body2" sx={{ flexBasis: '20%', flexShrink: 0 }}>
              {translate('module.feeManagement.platformFee')}
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <NumberInput
                source="platformFee"
                label=""
                placeholder={translate('module.feeManagement.platformFee')}
                validate={[required(), mustBeValidFeeFormat]}
                helperText={
                  <span
                    style={{}}
                  >{`${translate('module.game.config.min')} = 0.01%, ${translate('module.game.config.max')} = 2.5%, ${translate('module.game.config.step')} = 0.01%`}</span>
                }
                step={0.01}
                InputProps={{
                  endAdornment: <span style={{ marginLeft: 4, color: '#999' }}>%</span>, 
                }}
              />
            </Box>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
          >
            <Typography variant="body2" sx={{ flexBasis: '20%', flexShrink: 0 }}>
              {translate('module.feeManagement.xstockPlatformFee')}
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <NumberInput
                source="xstockPlatformFee"
                label=""
                placeholder={translate('module.feeManagement.xstockPlatformFee')}
                validate={[required(), mustBeValidFeeFormat]}
                helperText={
                  <span
                    style={{}}
                  >{`${translate('module.game.config.min')} = 0.01%, ${translate('module.game.config.max')} = 2.5%, ${translate('module.game.config.step')} = 0.01%`}</span>
                }
                step={0.01}
                InputProps={{
                  endAdornment: <span style={{ marginLeft: 4, color: '#999' }}>%</span>,
                }}
              />
            </Box>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
          >
            <PermissionGuard resource="fee-management" action="update" fallback={<></>}>
              <SaveButton />
            </PermissionGuard>
          </Stack>
        </Form>
      </Edit>
    </EditContextProvider>
  );
}
